import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { Trilha, TrilhaSelecao } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";

/**
 * Lista de trilhas e qual está ativa — o escopo de tudo (RN-01).
 *
 * Quatro estados, como manda o invariante 3. E `aplicar` existe porque toda
 * rota de trilha devolve a seleção inteira: depois de criar, arquivar ou
 * excluir, o estado novo vem pronto do servidor e não precisa ser deduzido
 * aqui. Deduzir seria a forma de o celular e o desktop discordarem.
 */

type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; trilhas: Trilha[]; ativaId: string | null }
  | { nome: "erro"; mensagem: string };

interface Valor {
  estado: Estado;
  recarregar: () => Promise<void>;
  aplicar: (selecao: TrilhaSelecao) => void;
}

const Contexto = createContext<Valor | null>(null);

export function TrilhasProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });

  const recarregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      const r = await pedir("trilha.listar");
      setEstado({ nome: "pronto", trilhas: r.trilhas, ativaId: r.ativaId });
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar suas trilhas.",
      });
    }
  }, []);

  const aplicar = useCallback((s: TrilhaSelecao) => {
    setEstado({ nome: "pronto", trilhas: s.trilhas, ativaId: s.ativaId });
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  return <Contexto.Provider value={{ estado, recarregar, aplicar }}>{children}</Contexto.Provider>;
}

export function useTrilhas(): Valor {
  const v = useContext(Contexto);
  if (!v) throw new Error("useTrilhas fora do TrilhasProvider.");
  return v;
}

/** A trilha ativa, ou `null` — que é estado de produto, não falha (RN-04). */
export function useTrilhaAtiva(): Trilha | null {
  const { estado } = useTrilhas();
  if (estado.nome !== "pronto" || !estado.ativaId) return null;
  return estado.trilhas.find((t) => t.id === estado.ativaId) ?? null;
}
